import useNode from './useNode'
import camelcaseKeys from 'camelcase-keys'
import { supabase } from '../supabaseClient'
import { NODE_METADATA_COLUMNS } from '../utilities/nodeMetadataColumns'
import { create } from 'zustand'

// ⚠ NOT kept live by realtime, deliberately (app#132). `node_metadata` is not a member of the
// `supabase_realtime` publication, so a postgres_changes channel on it joins and then never
// delivers an event — this store carried exactly that dead subscription until it was removed.
// Publishing the table was the alternative and was rejected on cost: Realtime evaluates the
// reader's RLS per row per subscriber, and every node in the fleet rewrites its metadata roughly
// once a minute, so the price is a standing per-session load that grows with the fleet, paid for
// facts that change on the order of an agent upgrade or a config edit. Freshness instead comes
// from re-fetching at the two moments the set can change: controller.loadState once nodes
// resolve, and useOrganizations.setActiveOrg on an org switch. The cost is that an in-session
// change lands only on the next reload — except the location of a node that registers or
// reconnects: `useNode`'s realtime handler re-fetches that one row (`needsMetadataRefresh`).

export interface NodeMetadataState {
  nodeMetadata: NodeMetadata[]
  set: (state: Partial<NodeMetadataState>) => void
  get: () => Promise<void>
  getOne: (nodeId: string) => Promise<void>
  getByNodeId: (nodeId: string) => NodeMetadata | undefined
  reset: () => void
}

const state = {
  nodeMetadata: [],
}

const useNodeMetadata = create<NodeMetadataState>()((set, get) => ({
  ...state,
  set,
  get: async () => {
    const loadedNodeIds = useNode.getState().nodes.map(node => node.id)
    if (!loadedNodeIds.length) return set({ nodeMetadata: [] })

    const { data, error } = await supabase.from('node_metadata').select(NODE_METADATA_COLUMNS).in('node_id', loadedNodeIds)

    if (error) console.warn(error)
    else {
      const nodeMetadata: NodeMetadata[] = data.map(metadata => camelcaseKeys(metadata))
      set({ nodeMetadata })
    }
  },
  // Re-fetch ONE node's row and replace it in the store. No row yet (the router writes it after
  // the node row) leaves the store as it is.
  getOne: async (nodeId: string) => {
    const { data, error } = await supabase
      .from('node_metadata')
      .select(NODE_METADATA_COLUMNS)
      .eq('node_id', nodeId)
      .maybeSingle()
    if (error) return console.warn(error)
    if (!data) return
    const row = camelcaseKeys(data) as NodeMetadata
    set({ nodeMetadata: [...get().nodeMetadata.filter(m => m.nodeId !== nodeId), row] })
  },
  getByNodeId: (nodeId: string) => {
    return get().nodeMetadata.find(metadata => metadata.nodeId === nodeId)
  },
  reset: () => set({ ...state }),
}))

export default useNodeMetadata
